/*
 * Normalizes TanStack Form field errors into the single message the UI should
 * render. This was introduced for Zod-backed multi-step forms where TanStack
 * keeps validation results per trigger (`onChange`, `onBlur`, `onSubmit`,
 * etc.), so stale blur/submit entries can linger in `errorMap` after a field
 * has become valid again.
 */

interface FieldErrorCandidate {
  state: {
    meta: {
      errorMap: {
        onBlur?: unknown;
        onChange?: unknown;
        onDynamic?: unknown;
        onMount?: unknown;
        onServer?: unknown;
        onSubmit?: unknown;
      };
      errors: unknown[];
      isDirty: boolean;
      isTouched: boolean;
      isValid: boolean;
    };
  };
}

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined;
  }

  // TanStack/Zod can surface nested issue arrays for a single validation cause.
  if (Array.isArray(error)) {
    for (const nestedError of error) {
      const nestedErrorMessage = getErrorMessage(nestedError);

      if (nestedErrorMessage) {
        return nestedErrorMessage;
      }
    }

    return undefined;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return undefined;
};

const getPriorityErrors = ({
  errorMap,
  isDirty,
}: FieldErrorCandidate['state']['meta']) => [
  // Prefer per-cause mapped errors over the flattened errors array.
  errorMap.onChange,
  errorMap.onDynamic,
  errorMap.onSubmit,
  isDirty ? undefined : errorMap.onBlur,
  errorMap.onServer,
  errorMap.onMount,
];

const hasAnyMappedError = ({
  errorMap,
}: FieldErrorCandidate['state']['meta']) =>
  Object.values(errorMap).some((error) => error !== undefined);

export const displayFieldError = (
  field: FieldErrorCandidate,
): string | undefined => {
  if (!field.state.meta.isTouched || field.state.meta.isValid) {
    return undefined;
  }

  for (const error of getPriorityErrors(field.state.meta)) {
    const errorMessage = getErrorMessage(error);

    if (errorMessage) {
      return errorMessage;
    }
  }

  if (hasAnyMappedError(field.state.meta)) {
    return undefined;
  }

  // Only fall back when TanStack has not recorded a cause-specific error entry.
  return getErrorMessage(field.state.meta.errors[0]);
};

export type { FieldErrorCandidate };

export type DisplayFieldError = typeof displayFieldError;
